// Artisans Dashboard - Espace client particulier
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Wrench, FileText, Hammer, Star, Bookmark, Clock,
  ChevronRight, CheckCircle, AlertCircle, Users, Loader2, Menu,
  Plus, Sparkles
} from 'lucide-react';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import CockpitParticulierBottomNav from '../../../components/layout/CockpitParticulierBottomNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEMO_DEVIS = [
  { id: 'demo-1', prestation_name: 'Rénovation salle de bain', status: 'received', responses_count: 3, responses: 3, created_at: new Date(Date.now() - 3 * 86400000).toISOString() },
  { id: 'demo-2', prestation_name: 'Installation climatisation', status: 'pending', responses_count: 0, responses: 0, created_at: new Date(Date.now() - 7 * 86400000).toISOString() },
  { id: 'demo-3', prestation_name: 'Peinture appartement 80m²', status: 'accepted', responses_count: 2, responses: 2, created_at: new Date(Date.now() - 14 * 86400000).toISOString() },
];

const DEMO_ARTISANS = [
  { id: 'a1', name: 'Plomberie Durand & Fils', shop_name: 'Plomberie Durand & Fils', metier: 'Plombier', specialty: 'Plombier', rating: 4.9, reviews_count: 128 },
  { id: 'a2', name: 'Léonard Martin', shop_name: 'Léonard Martin', metier: 'Menuisier', specialty: 'Menuisier', rating: 4.7, reviews_count: 85 },
  { id: 'a3', name: 'Maçonnerie Pro', shop_name: 'Maçonnerie Pro', metier: 'Maçon', specialty: 'Maçon', rating: 4.8, reviews_count: 213 },
];

function StatCard({ title, value, icon: Icon, color, link, loading }) {
  const CardContent = () => (
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon size={24} className="text-white" />
      </div>
      <div className="flex-1">
        {loading ? (
          <div className="h-6 w-8 bg-gray-200 rounded animate-pulse" />
        ) : (
          <p className="text-xl font-semibold text-gray-900">{value}</p>
        )}
        <p className="text-sm text-gray-500">{title}</p>
      </div>
      <ChevronRight size={20} className="text-[#1E3A5F] group-hover:text-orange-500 transition-colors" />
    </div>
  );

  if (link) {
    return (
      <Link to={link}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-orange-200 transition-all cursor-pointer group"
        >
          <CardContent />
        </motion.div>
      </Link>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm"
    >
      <CardContent />
    </motion.div>
  );
}

function DevisCard({ devis, onClick }) {
  const statusConfig = {
    pending: { label: 'En attente', bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock },
    received: { label: 'Devis reçus', bg: 'bg-blue-50', text: 'text-[#1E3A5F]', icon: CheckCircle },
    accepted: { label: 'Accepté', bg: 'bg-green-50', text: 'text-green-700', icon: CheckCircle },
    expired: { label: 'Expiré', bg: 'bg-gray-100', text: 'text-gray-500', icon: AlertCircle },
  };
  const status = statusConfig[devis.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  const count = devis.responses_count || devis.responses || 0;

  return (
    <div
      className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer group"
      onClick={onClick}
    >
      <div className="w-10 h-10 bg-[#1E3A5F]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-orange-100 transition-colors">
        <Wrench size={20} className="text-[#1E3A5F]" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-gray-900 truncate">{devis.prestation_name || devis.title}</h4>
        <p className="text-xs text-gray-500">{count} réponse{count > 1 ? 's' : ''}</p>
      </div>
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${status.bg} ${status.text}`}>
        <StatusIcon size={10} />
        {status.label}
      </span>
      <ChevronRight size={16} className="text-gray-400 group-hover:text-orange-500 transition-colors" />
    </div>
  );
}

function ArtisansDashboard({ onOpenMenu }) {
  const { user } = useCurrentUser();
  const navigate = useNavigate();

  const [stats, setStats] = useState({ devisEnCours: 2, artisansFavoris: 3, chantiersTermines: 1, avisLaisses: 1 });
  const [recentDevis, setRecentDevis] = useState(DEMO_DEVIS);
  const [artisansFavoris, setArtisansFavoris] = useState(DEMO_ARTISANS);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('client_token');

        const devisResponse = await fetch(`${API_URL}/api/artisans/my-devis-requests?per_page=5`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });

        if (devisResponse.ok) {
          const devisData = await devisResponse.json();
          const realDevis = devisData.devis_requests || [];
          if (realDevis.length > 0) {
            setRecentDevis(realDevis);
            setIsDemo(false);
            const devisStats = devisData.stats || {};
            setStats(prev => ({
              ...prev,
              devisEnCours: (devisStats.pending || 0) + (devisStats.received || 0),
            }));
          }
        }

        const favorisResponse = await fetch(`${API_URL}/api/ecosystem/profile/me/followed-artisans?per_page=3`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (favorisResponse.ok) {
          const favorisData = await favorisResponse.json();
          const mapped = (favorisData.followed_artisans || []).map(f => ({
            id: f.artisan?.seller_id || f.artisan_id,
            name: f.artisan?.company_name || 'Artisan',
            shop_name: f.artisan?.company_name,
            metier: f.artisan?.metier,
            rating: f.artisan?.rating,
            reviews_count: f.artisan?.prestations_count || 0,
          }));
          if (mapped.length > 0) {
            setArtisansFavoris(mapped);
            setStats(prev => ({ ...prev, artisansFavoris: favorisData.total || mapped.length }));
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDevisClick = () => {
    navigate('/cockpit-particulier/artisans/devis');
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            {onOpenMenu && (
              <button
                onClick={onOpenMenu}
                className="md:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Menu size={22} className="text-[#1E3A5F]" />
              </button>
            )}
            <div>
              <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mes Prestataires</h1>
              <p className="text-sm text-gray-500">Gère tes demandes de devis et artisans favoris</p>
            </div>
          </div>
          <Link
            to="/decrire-projet"
            className="flex items-center gap-2 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-orange-500/25"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Demande de devis</span>
            <span className="sm:hidden">Devis</span>
          </Link>
        </div>

        <div className="flex items-center gap-3 mt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-100">
            <Wrench size={14} className="text-orange-500" />
            <span className="text-xs font-medium text-gray-600">
              {loading ? '...' : `${stats.devisEnCours} devis en cours`}
            </span>
          </div>
          {isDemo && (
            <span className="px-2 py-1 bg-amber-50 border border-amber-200 rounded-lg text-[10px] font-medium text-amber-700">
              Aperçu démo
            </span>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard title="Devis en cours" value={stats.devisEnCours} icon={FileText}
          color="bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A]"
          link="/cockpit-particulier/artisans/devis" loading={loading} />
        <StatCard title="Artisans favoris" value={stats.artisansFavoris} icon={Bookmark}
          color="bg-gradient-to-br from-orange-500 to-orange-600"
          link="/cockpit-particulier/artisans/favoris" loading={loading} />
        <StatCard title="Chantiers terminés" value={stats.chantiersTermines} icon={Hammer}
          color="bg-gradient-to-br from-green-500 to-green-600"
          link="/cockpit-particulier/artisans/chantiers" loading={loading} />
        <StatCard title="Avis laissés" value={stats.avisLaisses} icon={Star}
          color="bg-gradient-to-br from-amber-500 to-amber-600"
          link="/cockpit-particulier/artisans/avis" loading={loading} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 flex-1 overflow-hidden">
        {/* Devis récents */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#1E3A5F]">Mes demandes de devis</h2>
            <Link to="/cockpit-particulier/artisans/devis" className="text-xs text-orange-500 font-medium hover:text-orange-600 flex items-center gap-1">
              Voir tout <ChevronRight size={12} />
            </Link>
          </div>
          <div className="p-3 space-y-2 flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={24} className="animate-spin text-orange-500" />
              </div>
            ) : (
              recentDevis.map(devis => (
                <DevisCard key={devis.id} devis={devis} onClick={handleDevisClick} />
              ))
            )}
          </div>
        </div>

        {/* Artisans favoris */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#1E3A5F]">Artisans favoris</h2>
            <Link to="/cockpit-particulier/artisans/favoris" className="text-xs text-orange-500 font-medium hover:text-orange-600 flex items-center gap-1">
              Voir tout <ChevronRight size={12} />
            </Link>
          </div>
          <div className="p-3 space-y-2 flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={24} className="animate-spin text-orange-500" />
              </div>
            ) : (
              artisansFavoris.map(artisan => (
                <Link key={artisan.id} to="/cockpit-particulier/artisans/favoris"
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer group block">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A] rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {artisan.name?.charAt(0) || 'A'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 truncate">{artisan.shop_name || artisan.name}</h4>
                    <p className="text-xs text-gray-500">{artisan.specialty || artisan.metier}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      <Star size={12} className="text-amber-500 fill-amber-500" />
                      <span className="text-sm font-medium text-[#1E3A5F]">{artisan.rating || '—'}</span>
                    </div>
                    <p className="text-[10px] text-gray-400">{artisan.reviews_count || 0} avis</p>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8A] rounded-xl p-4 mt-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white mb-0.5">Besoin de travaux ?</h3>
            <p className="text-xs text-white/80 mb-2">
              Décris ton projet et reçois jusqu'à 5 devis gratuits d'artisans qualifiés sous 48h.
            </p>
            <Link to="/decrire-projet"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors">
              Décrire mon projet <ChevronRight size={12} />
            </Link>
          </div>
        </div>
      </div>

      <CockpitParticulierBottomNav />
    </div>
  );
}

export default ArtisansDashboard;
